"use client";

import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { createLoacalisedAddToFavSchema, AddToFavSchema } from "@/lib/validation/add-to-fav";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Dispatch, SetStateAction, useState, useTransition } from "react";
import { toast } from "@/components/atoms/toast";
import { convertDate, getCalendarLocale } from "@/lib/utils";
import { Button } from "@/components/atoms/buttons";
import { CalendarIcon, Check, Loader2 } from "lucide-react";
import { Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import { useCustomer } from "@/hooks/useCustomer";
import { useWishlist } from "@/hooks/useWishlist";
import { usePathname, useRouter } from "next/navigation";

type AddToFavFormProps = {
  productId?: string;
  onsetShowAddToFavForm: Dispatch<SetStateAction<boolean>>;
  locale: Locales;
};

const AddToFavForm = ({ productId, onsetShowAddToFavForm, locale }: AddToFavFormProps) => {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const { customer } = useCustomer();
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const { addToFavSchema } = createLoacalisedAddToFavSchema(locale);

  const { createList, isListNameExists } = useWishlist();

  const form = useForm<AddToFavSchema>({
    resolver: zodResolver(addToFavSchema),
    defaultValues: {
      listName: "",
      date: {
        from: undefined,
        to: undefined,
      },
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = form;

  const onSubmit = (data: AddToFavSchema) => {
    if (!customer?.id) {
      toast.error({
        title: __("Please log in to create wishlists.", locale),
      });
      router.push(`${getPermalink(locale, "login")}?redirect=${pathname}`);
      return;
    }

    if (isListNameExists(data.listName)) {
      toast.error({
        title: __("A list with the same name already exists.", locale),
      });
      return;
    }

    startTransition(async () => {
      try {
        const dateRange =
          data.date.from && data.date.to
            ? {
                from: data.date.from.toLocaleDateString("en-GB"),
                to: data.date.to.toLocaleDateString("en-GB"),
              }
            : undefined;

        await createList(data.listName, productId, dateRange);

        // Success handling
        onsetShowAddToFavForm(false);
        reset();

        toast.success({
          title: __(productId ? "Product added to list successfully!" : "List created successfully!", locale),
        });
      } catch (error) {
        // Error handling
        console.error("Error creating wishlist:", error);
        toast.error({
          title: __("Failed to create wishlist. Please try again.", locale),
        });
      }
    });
  };

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="animate-in slide-in-from-bottom-4 fade-in space-y-2.5 rounded-md bg-[#F4F4F499] p-2.5 duration-300 ease-in-out sm:mx-2.5 sm:my-3"
      >
        <FormField
          control={control}
          name="listName"
          render={({ field }) => (
            <FormItem className="gap-1">
              <div className="flex flex-col gap-2.5">
                <FormLabel className="mb-0 text-base font-medium">
                  {__("Enter a list name", locale)} <span className="text-pink-primary">*</span>
                </FormLabel>
                <FormControl>
                  <div className="relative *:not-first:mt-4">
                    <Input
                      disabled={isPending}
                      className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-12 bg-white pe-40 placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                      placeholder={__("List name", locale)}
                      {...field}
                    />
                    <span className="text-primary-light absolute inset-y-0 end-0 pe-3.5 text-xs font-medium">
                      {__("Maximum 30 characters", locale)}
                    </span>
                  </div>
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="date"
          render={({ field }) => {
            const dateFromError = errors.date?.from;
            const dateToError = errors.date?.to;
            const hasDateError = dateFromError || dateToError;

            return (
              <FormItem className="flex flex-col">
                <FormLabel className="text-base font-medium">
                  {__("Date of the event", locale)} <span className="text-pink-primary">*</span>
                </FormLabel>
                <Popover open={isOpen} onOpenChange={setIsOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        disabled={isPending}
                        type="button"
                        onClick={() => setIsOpen(true)}
                        variant="secondary"
                        className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 [&>svg]:text-dark-primary hover:text-dark-primary text-primary-light h-12 justify-start gap-2 bg-white px-3 text-sm font-medium hover:bg-[#F4F4F499] focus-visible:ring-[2px]"
                      >
                        {field.value?.from && field.value?.to
                          ? `${convertDate(field.value.from, "dd MMM yyyy")} - ${convertDate(field.value.to, "dd MMM yyyy")}`
                          : __("Select dates", locale)}
                        <CalendarIcon aria-hidden="true" size={20} />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-full border-none p-0 shadow-none" align="start">
                    <Calendar
                      onClose={() => setIsOpen(false)}
                      setLocale={getCalendarLocale(locale)}
                      title={__("Event Dates", locale)}
                      selected={field.value}
                      mode="range"
                      onSelect={field.onChange}
                      autoFocus
                    />
                  </PopoverContent>
                </Popover>
                {hasDateError && (
                  <span className="text-pink-primary text-sm font-medium">{dateFromError?.message}</span>
                )}
              </FormItem>
            );
          }}
        />
        <Button
          variant="rounded"
          size="md"
          disabled={isPending}
          type="submit"
          className="font-cabinet-grotesk h-11 w-full text-base font-bold"
        >
          {isPending && <Loader2 className="animate-spin" />}
          {__("Add to list", locale)}
          <Check aria-hidden="true" size={20} />
        </Button>
      </form>
    </Form>
  );
};

export default AddToFavForm;
